import { collection, getDocs, limit, query } from "firebase/firestore";
import { db } from "../firebase";
import { getNicheMeta, NICHE_KEYS, normalizeNicheKey } from "../components/niche/nicheMeta";
import { fetchCreators } from "./userQueryService";

const RECENT_KEY = "recentSearches";
const MAX_RECENT = 8;

let catalogCache = null;
let catalogPromise = null;

export function isDropClosed(drop) {
  const max = drop.maxViews || 100;
  return drop.status === "closed" || Number(drop.viewCount ?? 0) >= max;
}

export function dropViewsLeft(drop) {
  return Math.max((drop.maxViews || 100) - Number(drop.viewCount ?? 0), 0);
}

export function dropProgressPct(drop) {
  const max = drop.maxViews || 100;
  return Math.min(100, Math.round((Number(drop.viewCount ?? 0) / max) * 100));
}

function normalizeQuery(q) {
  return (q || "").trim().toLowerCase();
}

/** Prefix + substring match (Firestore prefix until Algolia). */
export function matchesSearchQuery(values, query) {
  const q = normalizeQuery(query);
  if (!q) return false;
  return values.some((value) => {
    if (value == null || value === "") return false;
    const s = String(value).toLowerCase();
    return s.startsWith(q) || s.split(/\s+/).some((word) => word.startsWith(q)) || s.includes(q);
  });
}

export async function getSearchCatalog(force = false) {
  if (catalogCache && !force) return catalogCache;
  if (catalogPromise && !force) return catalogPromise;

  catalogPromise = (async () => {
    const [creators, dropsSnap] = await Promise.all([
      fetchCreators(100),
      getDocs(query(collection(db, "drops"), limit(100))),
    ]);

    const drops = dropsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const creatorById = Object.fromEntries(creators.map((c) => [c.uid, c]));

    catalogCache = { creators, drops, creatorById };
    return catalogCache;
  })();

  return catalogPromise;
}

export function searchCatalog(catalog, query) {
  const q = normalizeQuery(query);
  if (!q || !catalog) {
    return { creators: [], drops: [] };
  }

  const creators = catalog.creators
    .filter((c) =>
      matchesSearchQuery(
        [
          c.displayName,
          c.handle,
          c.username,
          c.location,
          getNicheMeta(c.niche).label,
          c.bio,
        ],
        q
      )
    )
    .map((c) => {
      const openDrops = catalog.drops.filter(
        (d) => d.creatorId === c.uid && !isDropClosed(d)
      ).length;
      return { ...c, openDropCount: openDrops };
    });

  const drops = catalog.drops
    .filter((d) => {
      const creator = catalog.creatorById[d.creatorId];
      return matchesSearchQuery(
        [
          d.title,
          d.caption,
          d.description,
          creator?.displayName,
          creator?.handle,
          creator?.username,
        ],
        q
      );
    })
    .map((d) => ({
      ...d,
      creator: catalog.creatorById[d.creatorId] || null,
      closed: isDropClosed(d),
      viewsLeft: dropViewsLeft(d),
      progressPct: dropProgressPct(d),
    }));

  return { creators, drops };
}

export function getNicheBrowseStats(catalog) {
  return NICHE_KEYS.map((key) => {
    const meta = getNicheMeta(key);
    const nicheCreators = catalog.creators.filter(
      (c) => normalizeNicheKey(c.niche) === key
    );
    const openDrops = catalog.drops.filter((d) => {
      const creator = catalog.creatorById[d.creatorId];
      return creator && normalizeNicheKey(creator.niche) === key && !isDropClosed(d);
    }).length;

    return {
      key,
      label: meta.label,
      creatorCount: nicheCreators.length,
      openDrops,
    };
  });
}

export function buildTrendingSearches(catalog) {
  const items = [];

  const topCreators = [...catalog.creators]
    .sort((a, b) => (b.followerCount || 0) - (a.followerCount || 0))
    .slice(0, 2);

  topCreators.forEach((c) => {
    items.push({
      text: c.displayName || c.handle || c.username || "Creator",
      type: "creator",
      meta: "Creator",
      query: c.displayName || c.handle || c.username,
    });
  });

  const openDrops = catalog.drops
    .filter((d) => !isDropClosed(d))
    .sort((a, b) => dropViewsLeft(a) - dropViewsLeft(b))
    .slice(0, 2);

  openDrops.forEach((d) => {
    const left = dropViewsLeft(d);
    items.push({
      text: d.title || "Untitled drop",
      type: "drop",
      meta: `Drop · ${left} left`,
      query: d.title || "",
    });
  });

  const nicheStats = getNicheBrowseStats(catalog)
    .filter((n) => n.creatorCount > 0)
    .sort((a, b) => b.creatorCount - a.creatorCount);

  if (nicheStats[0]) {
    items.push({
      text: nicheStats[0].label,
      type: "niche",
      meta: "Niche",
      query: nicheStats[0].label,
    });
  }

  if (items.length < 5 && catalog.creators[2]) {
    const c = catalog.creators[2];
    items.push({
      text: c.displayName || c.handle,
      type: "creator",
      meta: "Creator",
      query: c.displayName || c.handle,
    });
  }

  return items.slice(0, 5);
}

export function getRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(Boolean).slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

export function addRecentSearch(term) {
  const cleaned = (term || "").trim();
  if (!cleaned) return getRecentSearches();
  const prev = getRecentSearches().filter((s) => s.toLowerCase() !== cleaned.toLowerCase());
  const next = [cleaned, ...prev].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  return next;
}

export function removeRecentSearch(term) {
  const next = getRecentSearches().filter((s) => s !== term);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  return next;
}

export function clearRecentSearches() {
  localStorage.removeItem(RECENT_KEY);
  return [];
}

/** @deprecated Legacy helper — use searchCatalog via getSearchCatalog */
export async function searchPlatform(searchText) {
  const catalog = await getSearchCatalog();
  const { creators, drops } = searchCatalog(catalog, searchText);
  return { creators, moments: drops };
}
